import { Box, Paper, Stack, Typography, Divider, Skeleton, Button } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import UserAndActionDetails from './UserAndActionDetails';
import useResponsive from '../../../../Hooks/useResponsive';
import ActionAreaLanding from './ActionAreaLanding';
import NoDataFound from '../../../../components/NodataIndication/NoDataFound';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useReactToPrint } from 'react-to-print';

const FormParent = ({
  managerView,
  data,
  loading,
  appraisalCycleId,
  api,
  dispatchParams,
  formClose,
  viewOnly,
  userID,
  isAdmin,
}) => {
  const mdDown = useResponsive('down', 'md');
  const [downloadButtonClicked, setDownloadButtonClicked] = useState(false);
  let componentRef = useRef(null);
  const [submitOpen, setSubmitOpen] = useState(true);
  const [expandedParameterNames, setExpandedParameterNames] = useState([]);
  const allParametersName = Array.isArray(data?.parameters) ? data?.parameters.map((p) => p) : [];
  const isAllExpanded = expandedParameterNames.length === allParametersName.length;
  const [moduleAverages, setModuleAverages] = useState({});
  const toggleExpandAll = () => {
    setExpandedParameterNames(isAllExpanded ? [] : allParametersName);
  };

  const [finalDetail, setFinalDetail] = useState(
    managerView || viewOnly
      ? { ...data.finalDetail }
      : {
          finalScore: {
            user: 0,
            manager: 0,
            outOfscoreManger: 0,
            outOfScoreUser: 0,
          },
          rating: { user: 0, manager: 0 },
        }
  );

  useEffect(() => {
    finalScoreAndRatingCalculation();
  }, [moduleAverages]);

  const [allowedRatingModuleNamesAndItsScoresForUsers, setAllowedRatingModuleNamesAndItsScoresForUsers] = useState([]);
  const [allowedRatingModuleNamesAndItsScoresForManagers, setAllowedRatingModuleNamesAndItsScoresManagers] = useState(
    []
  );

  const finalScoreAndRatingCalculation = () => {
    const moduleNames = Object.keys(moduleAverages); // e.g. ["kra", "goals", ...]

    const moduleWeights = data?.rating?.moduleWeighting?.weights || {};
    // e.g. { "Goals":30, "KRA":50, "Skill & Will":20 }
    const normalizedWeights = {};

    moduleNames.forEach((modName) => {
      // FILTERING ONLY REQUIRED MODULES AND ITS WEIGHTAGE AND COMBINE THE MOUDULE AND ITS WEIGHTAGE  e.g. { "Goals":30, "KRA":50, }
      let foundWeight = null;

      for (const weightKey in moduleWeights) {
        const normalize = (str) => str.toString().toLowerCase().replace(/[\s&]/g, '').replace(/_/g, '');
        if (normalize(weightKey) === normalize(modName)) {
          foundWeight = moduleWeights[weightKey];
          break;
        }
      }

      normalizedWeights[modName] = foundWeight !== null ? foundWeight : 100;
      //  normalizedWeights[modName] = foundWeight || 100;
    });

    // console.log('moduleAverages', moduleAverages);
    // console.log('modulenames', moduleNames);
    // console.log('moduleWeights', moduleWeights);
    // console.log('normalizedWeights', moduleWeights);

    let userWeightedSum = 0;
    let managerWeightedSum = 0;
    let totalWeightOfUser = 0;
    let totalWeightOfManager = 0;

    //FILTERING THE ONLY ALLOWED RATING MODULENAMES FOR USERS BY CHECKING USER.RATING==TRUE AND ALSO ENSURING ITS EXIST IN MODULENAMES
    const moduleNamesOfRatingAllowedForUsers = moduleNames.filter((m) => {
      return data?.config?.selfApparaisal?.parameters?.some((item) => {
        const normalizedName = item.name?.trim().toLowerCase();
        return item.ratings === true && normalizedName === m.toLowerCase();
      });
    });

    // console.log('moduleNamesOfRatingAllowedForUsers', moduleNamesOfRatingAllowedForUsers);

    //FILTERING THE ONLY ALLOWED RATING MODULENAMES FOR MANAGERS BY CHECKING USER.RATING==TRUE AND ALSO ENSURING ITS EXIST IN MODULENAMES
    const moduleNamesOfRatingAllowedForManagers = moduleNames.filter((m) => {
      return data?.config?.managerReview?.parameters?.some((item) => {
        const normalizedName = item.name?.trim().toLowerCase();
        return item.ratings === true && normalizedName === m.toLowerCase();
      });
    });

    // console.log("moduleNamesOfRatingAllowedForManagers",moduleNamesOfRatingAllowedForManagers);

    const maxRating = data?.rating?.range?.max;

    let tempAllowedRatingModuleNamesAndItsScoresForUsers = [];
    let tempAllowedRatingModuleNamesAndItsScoresManagers = [];

    //ONLY TOTAL SCORE CALCULATING FROM THE USER ALLOWED FIELDS
    moduleNamesOfRatingAllowedForUsers.forEach((name) => {
      const weight = (Number(normalizedWeights[name]) || 100) / 100; // normalize to 0–1
      const userRating = moduleAverages[name].user || 0;

      userWeightedSum += weight * userRating;
      totalWeightOfUser += weight;
      tempAllowedRatingModuleNamesAndItsScoresForUsers.push({
        moduleName: name,
        ratedScore: userRating,
        maximumRating: maxRating,
      });
    });

    // console.log('tempAllowedRatingModuleNamesAndItsScoresForUsers', tempAllowedRatingModuleNamesAndItsScoresForUsers);

    setAllowedRatingModuleNamesAndItsScoresForUsers(tempAllowedRatingModuleNamesAndItsScoresForUsers);

    moduleNamesOfRatingAllowedForManagers.forEach((name) => {
      const weight = (Number(normalizedWeights[name]) || 100) / 100; // normalize to 0–1
      const managerRating = moduleAverages[name].manager || 0;
      // console.log("name",name,"managerRating",managerRating);

      managerWeightedSum += weight * managerRating;
      totalWeightOfManager += weight;
      tempAllowedRatingModuleNamesAndItsScoresManagers.push({
        moduleName: name,
        ratedScore: managerRating,
        maximumRating: maxRating,
      });
    });
    setAllowedRatingModuleNamesAndItsScoresManagers(tempAllowedRatingModuleNamesAndItsScoresManagers);
    // console.log("managerWeightedSum",managerWeightedSum);

    // --- avoid division by zero ---
    const userTotalScore = totalWeightOfUser > 0 ? userWeightedSum / totalWeightOfUser : 0;
    const managerTotalScore = totalWeightOfManager > 0 ? managerWeightedSum / totalWeightOfManager : 0;

    const decimalPoints = data?.rating?.decimalPoints || 1;

    const userRatingString = findRatingLabel(userTotalScore, data?.rating?.ratingScale || []);
    const managerRatingString = findRatingLabel(managerTotalScore, data?.rating?.ratingScale || []);

    setFinalDetail({
      finalScore: {
        user: Number(userTotalScore.toFixed(decimalPoints)),
        manager: Number(managerTotalScore.toFixed(decimalPoints)),
        outOfScoreUser: maxRating,
        outOfscoreManger: maxRating,
      },
      rating: {
        user: userRatingString,
        manager: managerRatingString,
      },
    });
  };

  const findRatingLabel = (score, ratingScale) => {
    if (typeof score !== 'number') return null;

    // find the first range that matches
    const match = ratingScale.find((item) => score >= item.min && score <= item.max);

    return match ? match.label : 'No Rating';
  };

  const downloadPdfTolocal = useReactToPrint({
    //BROWSER PREVIEW PAGE TO DOWNLOAD AND PRINT
    content: () => componentRef.current,
    documentTitle: `Self Appraisal`,
    onPrintError: () => alert('error occured'),
  });

  useEffect(() => {
    if (downloadButtonClicked && submitOpen && isAllExpanded) {
      const timer = setTimeout(() => {
        downloadPdfTolocal();
        setDownloadButtonClicked(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [downloadButtonClicked, submitOpen, expandedParameterNames]);

  const downloadPdfButtonCickFunction = () => {
    setExpandedParameterNames(allParametersName);
    setSubmitOpen(true);
    setDownloadButtonClicked(true);
  };
  return (
    <>
      {!loading && Object.keys(data).length ? (
        <Box>
          {isAdmin && (
            <Box mb={1} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Button
                startIcon={<Icon icon="ion:print-outline" height="22" width="22" />}
                size="small"
                sx={{ boxShadow: 'none' }}
                variant="contained"
                onClick={downloadPdfButtonCickFunction}
              >
                Download Report
              </Button>
            </Box>
          )}

          <Box
            className="print-content"
            ref={componentRef}
            // border={1}
            sx={{
              display: 'flex',
              flexDirection: mdDown || downloadButtonClicked ? 'column' : 'row',
              gap: 2,
            }}
          >
            {/* Left Panel */}
            <Paper
              elevation={downloadButtonClicked?0:6}
              sx={{
                width: mdDown || downloadButtonClicked ? '100%' : '20%',
                p: 1,
                // order: mdDown ? 2 : 1,
                // height: "83.2vh",
                overflow: 'hidden',
              }}
            >
              <UserAndActionDetails
                user={data?.user}
                parameters={data?.parameters}
                finalDetail={finalDetail}
                moduleAverages={moduleAverages}
                managerView={managerView}
              />
            </Paper>

            {/* Right Panel */}
            <Paper
              elevation={6}
              sx={{
                width: mdDown || downloadButtonClicked ? '100%' : '80%',
                p: 1,
                // order: 1,
                // height: "83.2vh",
                overflow: 'hidden',
              }}
            >
              <ActionAreaLanding
                parameters={data?.parameters}
                ApiData={data}
                moduleAverages={moduleAverages}
                setModuleAverages={setModuleAverages}
                allowedRatingModuleNamesAndItsScoresForUsers={allowedRatingModuleNamesAndItsScoresForUsers}
                allowedRatingModuleNamesAndItsScoresForManagers={allowedRatingModuleNamesAndItsScoresForManagers}
                finalDetail={finalDetail}
                managerView={managerView}
                appraisalCycleId={appraisalCycleId}
                api={api}
                dispatchParams={dispatchParams}
                formClose={formClose}
                viewOnly={viewOnly}
                userID={userID}
                downloadButtonClicked={downloadButtonClicked}
                submitOpen={submitOpen}
                setSubmitOpen={setSubmitOpen}
                expandedParameterNames={expandedParameterNames}
                setExpandedParameterNames={setExpandedParameterNames}
                allParametersName={allParametersName}
                isAllExpanded={isAllExpanded}
                toggleExpandAll={toggleExpandAll}
              />
            </Paper>
          </Box>
        </Box>
      ) : loading ? (
        // Show Skeletons while loading
        <Box
          sx={{
            display: 'flex',
            flexDirection: mdDown ? 'column' : 'row',
            gap: 2,
          }}
        >
          <Paper
            elevation={6}
            sx={{
              width: mdDown ? '100%' : '20%',
              p: 1,
              order: mdDown ? 2 : 1,
              // height: "83.2vh",
              overflow: 'hidden',
            }}
          >
            <Box
              height={'100%'}
              width={'100%'}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Skeleton variant="rectangular" width="100%" height="30%" />
              <Skeleton variant="rectangular" width="100%" height="70%" />
            </Box>
          </Paper>

          <Paper
            elevation={6}
            sx={{
              width: mdDown ? '100%' : '80%',
              p: 1,
              order: 1,
              // height: '83.2vh',
              overflow: 'hidden',
            }}
          >
            <Box
              height="100%"
              width="100%"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {[...Array(11)].map((_, index) => (
                <Skeleton key={index} variant="rectangular" width="100%" height={40} />
              ))}
            </Box>
          </Paper>
        </Box>
      ) : (
        <NoDataFound />
      )}
    </>
  );
};

export default React.memo(FormParent);
